import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import mermaid from 'mermaid';

mermaid.initialize({
  startOnLoad: false,
  theme: 'neutral',
  securityLevel: 'strict',
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
});

let mermaidIdCounter = 0;
const codeBlockChrome = {
  border: '1px solid #27272a',
  headerBg: '#1f1f23',
  headerBorder: '#2d2d32',
  bodyBg: '#111318',
};

const cleanedOneDark = {
  ...oneDark,
  'pre[class*="language-"]': {
    ...oneDark['pre[class*="language-"]'],
    background: codeBlockChrome.bodyBg,
    textShadow: 'none',
    fontFamily: '"SF Mono", SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
  },
  'code[class*="language-"]': {
    ...oneDark['code[class*="language-"]'],
    background: 'transparent',
    textShadow: 'none',
    fontFamily: '"SF Mono", SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
  },
};

function cleanupMermaidRender(id) {
  for (const sel of [`#${id}`, `#d${id}`, `[data-id="${id}"]`]) {
    try { document.querySelector(sel)?.remove(); } catch { /* ignore */ }
  }
}

function CopyBtn({ text, light }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top" arrow>
      <IconButton
        onClick={handleCopy}
        size="small"
        sx={{
          width: 26,
          height: 26,
          borderRadius: '6px',
          color: copied ? '#34d399' : (light ? '#a1a1aa' : 'rgba(255,255,255,0.4)'),
          transition: 'all 150ms ease',
          '&:hover': {
            backgroundColor: light ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)',
            color: copied ? '#34d399' : (light ? '#3f3f46' : 'rgba(255,255,255,0.8)'),
          },
        }}
      >
        {copied
          ? <CheckIcon sx={{ fontSize: 14 }} />
          : <ContentCopyIcon sx={{ fontSize: 14 }} />
        }
      </IconButton>
    </Tooltip>
  );
}

function MermaidBlock({ chart }) {
  const containerRef = useRef(null);
  const [svg, setSvg] = useState('');
  const [failed, setFailed] = useState(false);
  const [view, setView] = useState('diagram');

  useEffect(() => {
    if (!chart) return;
    let cancelled = false;
    const id = `mmd${mermaidIdCounter++}`;

    const sandbox = document.createElement('div');
    sandbox.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;overflow:hidden;visibility:hidden;';
    document.body.appendChild(sandbox);

    (async () => {
      try {
        const { svg: renderedSvg } = await mermaid.render(id, chart, sandbox);
        if (!cancelled) setSvg(renderedSvg);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        sandbox.remove();
        cleanupMermaidRender(id);
      }
    })();

    return () => {
      cancelled = true;
      sandbox.remove();
      cleanupMermaidRender(id);
    };
  }, [chart]);

  const showCode = failed || view === 'code';
  const canToggle = !failed && svg;

  return (
    <Box
      sx={{
        my: 1.5,
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid #e4e4e7',
        backgroundColor: showCode ? '#1e1e1e' : '#fff',
        transition: 'background-color 200ms ease',
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.25,
          height: 36,
          backgroundColor: showCode ? '#282828' : '#fafafa',
          borderBottom: `1px solid ${showCode ? '#333' : '#f0f0f0'}`,
          transition: 'all 200ms ease',
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: showCode ? 'rgba(255,255,255,0.45)' : '#a1a1aa',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            mr: 'auto',
          }}
        >
          Mermaid
        </Box>

        {canToggle && (
          <Box
            sx={{
              display: 'flex',
              height: 24,
              borderRadius: '6px',
              backgroundColor: showCode ? 'rgba(255,255,255,0.06)' : '#f0f0f0',
              p: '2px',
              transition: 'background-color 200ms ease',
            }}
          >
            {['diagram', 'code'].map((v) => (
              <Box
                key={v}
                onClick={() => setView(v)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  borderRadius: '5px',
                  fontSize: '11px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 150ms ease',
                  color: view === v
                    ? (showCode ? '#fff' : '#18181b')
                    : (showCode ? 'rgba(255,255,255,0.4)' : '#a1a1aa'),
                  backgroundColor: view === v
                    ? (showCode ? 'rgba(255,255,255,0.1)' : '#fff')
                    : 'transparent',
                  boxShadow: view === v ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                  '&:hover': {
                    color: view === v
                      ? (showCode ? '#fff' : '#18181b')
                      : (showCode ? 'rgba(255,255,255,0.65)' : '#52525b'),
                  },
                }}
              >
                {v === 'diagram' ? 'Preview' : 'Code'}
              </Box>
            ))}
          </Box>
        )}

        <CopyBtn text={chart} light={!showCode} />
      </Box>

      {/* Content */}
      {showCode ? (
        <SyntaxHighlighter
          style={cleanedOneDark}
          language="text"
          PreTag="div"
          wrapLongLines
          customStyle={{
            margin: 0,
            borderRadius: 0,
            fontSize: '12.5px',
            lineHeight: 1.65,
            padding: '14px 16px',
            background: codeBlockChrome.bodyBg,
            overflowX: 'auto',
          }}
        >
          {chart}
        </SyntaxHighlighter>
      ) : svg ? (
        <Box
          ref={containerRef}
          sx={{
            p: 2.5,
            display: 'flex',
            justifyContent: 'center',
            overflowX: 'auto',
            '& svg': { maxWidth: '100%', height: 'auto', display: 'block' },
          }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      ) : null}
    </Box>
  );
}

function CodeBlock({ inline, className, children, node: _node, ...props }) {
  const codeString = String(children).replace(/\n$/, '');
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';

  if (inline) {
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  }

  if (language === 'mermaid') {
    return <MermaidBlock chart={codeString} />;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        my: 1.5,
        borderRadius: '10px',
        overflow: 'hidden',
        border: codeBlockChrome.border,
        '&:hover .code-copy-btn': { opacity: 1 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.25,
          height: 36,
          backgroundColor: codeBlockChrome.headerBg,
          borderBottom: `1px solid ${codeBlockChrome.headerBorder}`,
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: 'rgba(255,255,255,0.45)',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            mr: 'auto',
          }}
        >
          {language || 'code'}
        </Box>
        <Box className="code-copy-btn" sx={{ opacity: 0, transition: 'opacity 150ms ease' }}>
          <CopyBtn text={codeString} />
        </Box>
      </Box>

      <SyntaxHighlighter
        style={cleanedOneDark}
        language={language || 'text'}
        PreTag="div"
        wrapLongLines
        customStyle={{
          margin: 0,
          borderRadius: 0,
          fontSize: '12.5px',
          lineHeight: 1.65,
          padding: '14px 16px',
          background: codeBlockChrome.bodyBg,
          overflowX: 'auto',
        }}
        {...props}
      >
        {codeString}
      </SyntaxHighlighter>
    </Box>
  );
}

const markdownSx = {
  '& p': { m: 0 },
  '& p + p': { mt: 1 },
  '& strong': { fontWeight: 700, color: 'var(--color-text-primary)' },
  '& em': { fontStyle: 'italic' },
  '& a': {
    color: '#4f46e5',
    textDecoration: 'none',
    borderBottom: '1px solid transparent',
    transition: 'border-color 150ms ease',
    '&:hover': { borderBottom: '1px solid #4f46e5' },
  },
  '& ul, & ol': { m: 0, mt: 0.5, mb: 0.5, pl: 2.5, '& li': { mb: 0.25 } },
  '& ul': { listStyleType: 'disc' },
  '& ol': { listStyleType: 'decimal' },
  '& code': {
    fontFamily: '"SF Mono", SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
    fontSize: '0.85em',
    backgroundColor: 'var(--color-action-hover)',
    borderRadius: '5px',
    px: 0.7,
    py: 0.2,
    color: '#d6336c',
    fontWeight: 500,
  },
  '& pre': { m: 0, '& code': { backgroundColor: 'transparent', border: 'none', p: 0, color: 'inherit', fontWeight: 400 } },
  '& blockquote': {
    m: 0,
    mt: 1,
    mb: 1,
    ml: 0,
    pl: 1.5,
    borderLeft: '2px solid var(--color-divider)',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
  },
  '& hr': { border: 'none', borderTop: '1px solid var(--color-divider)', my: 2 },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    my: 1.5,
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid var(--color-divider)',
  },
  '& th, & td': {
    border: '1px solid var(--color-divider)',
    px: 1.25,
    py: 0.75,
    fontSize: 'inherit',
    textAlign: 'left',
  },
  '& th': { backgroundColor: 'var(--color-bg-subtle)', fontWeight: 600, color: 'var(--color-text-primary)', fontSize: '0.9em' },
  '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 0.75, lineHeight: 1.3, fontWeight: 700, color: 'var(--color-text-primary)' },
  '& h1': { fontSize: '1.3em' },
  '& h2': { fontSize: '1.2em' },
  '& h3': { fontSize: '1.1em' },
};

const markdownComponents = {
  code: CodeBlock,
};

function unwrapMarkdownFence(value) {
  if (typeof value !== 'string') return value;
  const match = value.match(/^\s*```(?:md|markdown)\s*\n([\s\S]*?)\n```\s*$/i);
  return match ? match[1] : value;
}

export default function MarkdownContent({ children, sx }) {
  const normalizedContent = unwrapMarkdownFence(children);

  return (
    <Box sx={{ ...sx, ...markdownSx }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {normalizedContent}
      </ReactMarkdown>
    </Box>
  );
}
